import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CreditCard, Building2, Landmark, FileText, Plus, Loader2 } from 'lucide-react';
import { useCustomerPaymentMethods, CustomerPaymentMethod } from '@/hooks/useCustomerPaymentMethods';
import { usePlatformBankDetails } from '@/hooks/usePlatformBankDetails';
import { usePlatformInvoicingSetting } from '@/hooks/usePlatformInvoicingSetting';
import { Skeleton } from '@/components/ui/skeleton';
import { usePaymentProcessingEnabled } from '@/hooks/usePaymentProcessingEnabled';

interface PaymentMethodSelectorProps {
  organizationId: string;
  selectedPaymentMethodId?: string;
  onSelect: (paymentMethodId: string) => void;
  showTraditionalAch?: boolean;
  showStripeInvoice?: boolean;
  onAddPaymentMethod?: () => void;
  isAddingPaymentMethod?: boolean;
}

export const TRADITIONAL_ACH_ID = 'traditional_ach';
export const STRIPE_INVOICE_ID = 'stripe_invoice';

export function PaymentMethodSelector({
  organizationId, 
  selectedPaymentMethodId, 
  onSelect,
  showTraditionalAch = true,
  showStripeInvoice = true,
  onAddPaymentMethod,
  isAddingPaymentMethod = false
}: PaymentMethodSelectorProps) {
  const { paymentMethods, isLoading } = useCustomerPaymentMethods(organizationId);
  const { data: bankDetails, isLoading: bankDetailsLoading } = usePlatformBankDetails();
  const { data: invoicingEnabled, isLoading: invoicingLoading } = usePlatformInvoicingSetting();

  const { paymentProcessingEnabled, isLoading: flagLoading } = usePaymentProcessingEnabled();

  if (flagLoading || !paymentProcessingEnabled) return null;

  const isTraditionalAchEnabled = bankDetails?.enabled && 
    bankDetails?.bankName && 
    bankDetails?.routingNumber && 
    bankDetails?.accountNumber;
  
  const isStripeInvoicingEnabled = invoicingEnabled === true;

  if (isLoading || bankDetailsLoading || invoicingLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  if ((!paymentMethods || paymentMethods.length === 0) && !isTraditionalAchEnabled && !isStripeInvoicingEnabled && !onAddPaymentMethod) {
    return (
      <div className="p-4 border border-dashed rounded-lg text-center text-muted-foreground">
        <p>No payment methods available. Please add a payment method first.</p>
      </div>
    );
  }

  const formatPaymentMethodDisplay = (pm: CustomerPaymentMethod) => {
    if (pm.payment_method_type === 'us_bank_account') {
      return `${pm.bank_name || 'Bank Account'} ••••${pm.payment_method_last4}`;
    }
    return `${pm.card_brand?.toUpperCase() || 'Card'} ••••${pm.payment_method_last4}`;
  };

  return (
    <RadioGroup 
      value={selectedPaymentMethodId} 
      onValueChange={onSelect}
      className="space-y-3"
    >
      {paymentMethods?.map((pm) => (
        <div 
          key={pm.id} 
          className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-accent/50 transition-colors"
        >
          <RadioGroupItem value={pm.stripe_payment_method_id} id={pm.stripe_payment_method_id} />
          <Label 
            htmlFor={pm.stripe_payment_method_id} 
            className="flex-1 cursor-pointer flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              {pm.payment_method_type === 'us_bank_account' ? (
                <Building2 className="h-5 w-5 text-muted-foreground" />
              ) : (
                <CreditCard className="h-5 w-5 text-muted-foreground" />
              )}
              <div>
                <div className="font-medium">
                  {formatPaymentMethodDisplay(pm)}
                </div>
                {pm.nickname && (
                  <div className="text-sm text-muted-foreground mt-1">
                    {pm.nickname}
                  </div>
                )}
              </div>
            </div>
            {pm.is_default && (
              <Badge variant="secondary" className="ml-2">Default</Badge>
            )}
          </Label>
        </div>
      ))}
      
      {/* Traditional ACH Option */}
      {showTraditionalAch && isTraditionalAchEnabled && (
        <div 
          className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-accent/50 transition-colors border-dashed"
        >
          <RadioGroupItem value={TRADITIONAL_ACH_ID} id={TRADITIONAL_ACH_ID} />
          <Label 
            htmlFor={TRADITIONAL_ACH_ID} 
            className="flex-1 cursor-pointer flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <Landmark className="h-5 w-5 text-blue-600" />
              <div>
                <div className="font-medium">Traditional ACH (Wire Transfer)</div>
                <div className="text-sm text-muted-foreground mt-1">
                  Transfer directly to our bank account
                </div>
              </div>
            </div>
            <Badge variant="outline" className="ml-2 text-green-600 border-green-600">No Fee</Badge>
          </Label>
        </div>
      )}
      
      {/* Stripe Invoice Option */}
      {showStripeInvoice && isStripeInvoicingEnabled && (
        <div 
          className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-accent/50 transition-colors border-dashed"
        >
          <RadioGroupItem value={STRIPE_INVOICE_ID} id={STRIPE_INVOICE_ID} />
          <Label 
            htmlFor={STRIPE_INVOICE_ID} 
            className="flex-1 cursor-pointer flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-purple-600" />
              <div>
                <div className="font-medium">Invoice Payment</div>
                <div className="text-sm text-muted-foreground mt-1">
                  Receive an invoice • Pay via bank transfer • Due on receipt • Auto-reminders
                </div>
              </div>
            </div>
            <Badge variant="outline" className="ml-2 text-purple-600 border-purple-600">Invoice</Badge>
          </Label>
        </div>
      )}
      
      {/* Add New Payment Method Option */}
      {onAddPaymentMethod && (
        <Button
          variant="outline"
          className="w-full justify-start gap-3 p-4 h-auto border-dashed"
          onClick={onAddPaymentMethod}
          disabled={isAddingPaymentMethod}
        >
          {isAddingPaymentMethod ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : (
            <Plus className="h-5 w-5 text-muted-foreground" />
          )}
          <div className="text-left">
            <div className="font-medium">
              {isAddingPaymentMethod ? 'Redirecting to Stripe...' : 'Add New Payment Method'}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              Set up a new card or bank account
            </div>
          </div>
        </Button>
      )}
    </RadioGroup>
  );
}